import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatOnboarding/Steps/DiagnosisUnlockFlow/diagnosisUnlockChrome";
/**
 * DiagnosisUnlockFlow chrome 默认值与 deep-merge。
 */

import type {
  DiagnosisUnlockModalChrome,
  DiagnosisUnlockModalChromePartial,
  DiagnosisUnlockStepChrome,
  DiagnosisUnlockStepChromePartial,
  DiagnosisUnlockTopNavConfig,
} from "./types";
import { getDefaultStepProgressLabels } from "./LoadingStep/diagnosisUnlockFourStep.contract";
import { trackOnboardingAnalytics } from "@/src/hooks/onboarding/onboardingAnalytics";

const I18n = I18nUtil.createScoped(Translations);

/** Preset 常用 action 常量（调用方可复用或自定义 string） */
export const DIAGNOSIS_UNLOCK_ACTIONS = {
  noop: "noop",
  advanceToStep3: "advance_to_step3",
  step1Exit: "step1_exit",
  reportClose: "report_close",
  unlockFullPlan: "unlock_full_plan",
  setupGoal: "setup_goal",
  viewMyGoal: "view_my_goal",
  maybeLater: "maybe_later",
} as const;

/**
 * Onboarding step3 底栏 — 由**调用方**按 `isMember` 传入；Flow 内不根据会员改按钮。
 *
 * | isMember | 主按钮 | 次按钮 |
 * |----------|--------|--------|
 * | false | Unlock full plan | Maybe later |
 * | true | Setup plan | 隐藏 |
 *
 * `hideActionSummarySection` 由调用方按会员态传入（见 `diagnosisUnlockPlanPreviewLogic`）。
 */
export type GetOnboardingDiagnosisUnlockChromeOptions = {
  /** 已有 tool result、直接打开 step2 时展示顶栏关闭按钮 */
  hasResult?: boolean;
};

export function getOnboardingDiagnosisUnlockChrome(
  isMember: boolean,
  options?: GetOnboardingDiagnosisUnlockChromeOptions,
): DiagnosisUnlockModalChromePartial {
  const chrome: DiagnosisUnlockModalChromePartial = {};
  const hideStep3Ctas = options?.hasResult === true;
  chrome.step3 = {
    secondaryCta: {
      visible: true,
      onClick: () => {
        trackOnboardingAnalytics("ob_maybe_later_click");
      },
    },
    primaryCta: {
      visible: true,
      onClick: () => {
        trackOnboardingAnalytics("ob_unlock_full_plan_click");
      },
    },
  };

  if (options?.hasResult) {
    chrome.step2 = {
      topNav: {
        variant: "close",
        behavior: "dismiss",
        dismissAction: DIAGNOSIS_UNLOCK_ACTIONS.reportClose,
        onClick: () => {
          trackOnboardingAnalytics("ob_report_close");
        },
      },
    };
  }

  if (isMember || hideStep3Ctas) {
    chrome.step3 = {};
    chrome.step3.topNav = {
      variant: "close",
      behavior: "dismiss",
      dismissAction: DIAGNOSIS_UNLOCK_ACTIONS.reportClose,
    };
    chrome.step3.secondaryCta = { visible: false };

    if (isMember) {
      chrome.step3.primaryCta = {
        label: I18n.t("Setup plan"),
        action: DIAGNOSIS_UNLOCK_ACTIONS.setupGoal,
        onClick: () => {
          trackOnboardingAnalytics("ob_report_setup_plan_click");
        },
        visible: !hideStep3Ctas,
      };
    }
  }

  return chrome;
}

/** Plan View report — step3 CTA（由调用方按 Goal.status 传入 hasEstablishedGoal） */
export function getPlanReportDiagnosisUnlockChrome(
  hasEstablishedGoal: boolean,
): DiagnosisUnlockModalChromePartial {
  return {
    step3: {
      primaryCta: {
        label: hasEstablishedGoal
          ? I18n.t("View my goal")
          : I18n.t("Setup goal"),
        action: hasEstablishedGoal
          ? DIAGNOSIS_UNLOCK_ACTIONS.viewMyGoal
          : DIAGNOSIS_UNLOCK_ACTIONS.setupGoal,
      },
      secondaryCta: {
        label: I18n.t("Maybe later"),
        action: DIAGNOSIS_UNLOCK_ACTIONS.maybeLater,
        visible: true,
      },
    },
  };
}

/** fetch 路径：Plan step3 label/action 打底，调用方 `chrome.step3` 可覆盖 onClick 等 */
export function mergePlanFetchChromePartial(
  callerChrome: DiagnosisUnlockModalChromePartial | undefined,
  planChrome: DiagnosisUnlockModalChromePartial,
): DiagnosisUnlockModalChromePartial {
  const callerStep3 = callerChrome?.step3;

  return {
    ...callerChrome,
    step3: {
      ...planChrome.step3,
      ...callerStep3,
      primaryCta: {
        ...planChrome.step3?.primaryCta,
        ...callerStep3?.primaryCta,
      },
      secondaryCta: {
        ...planChrome.step3?.secondaryCta,
        ...callerStep3?.secondaryCta,
      },
    },
  };
}

/** 默认 chrome（step3 为非会员 Onboarding；会员请用 {@link getOnboardingDiagnosisUnlockChrome}） */
function getDefaultDiagnosisUnlockChrome(): DiagnosisUnlockModalChrome {
  return {
    step1: {
      showStepProgress: getDefaultStepProgressLabels(),
      topNav: { variant: "none" } satisfies DiagnosisUnlockTopNavConfig,
      primaryCta: { label: "_", action: DIAGNOSIS_UNLOCK_ACTIONS.noop },
    },
    step2: {
      topNav: { variant: "none" },
      primaryCta: {
        label: I18n.t("See what you should do"),
        action: DIAGNOSIS_UNLOCK_ACTIONS.advanceToStep3,
      },
    },
    step3: {
      topNav: { variant: "back", behavior: "previous_step" },
      primaryCta: {
        label: I18n.t("Unlock full plan"),
        action: DIAGNOSIS_UNLOCK_ACTIONS.unlockFullPlan,
      },
      secondaryCta: {
        label: I18n.t("Maybe later"),
        action: DIAGNOSIS_UNLOCK_ACTIONS.maybeLater,
        visible: true,
      },
    },
  };
}

function mergeStepChrome(
  base: DiagnosisUnlockStepChrome,
  override?: DiagnosisUnlockStepChromePartial,
): DiagnosisUnlockStepChrome {
  if (!override) {
    return base;
  }

  const primaryCta = { ...base.primaryCta, ...override.primaryCta };
  const secondaryCta =
    override.secondaryCta != null
      ? {
          ...(base.secondaryCta ?? {
            label: I18n.t("Maybe later"),
            action: DIAGNOSIS_UNLOCK_ACTIONS.maybeLater,
            visible: false,
          }),
          ...override.secondaryCta,
        }
      : base.secondaryCta;

  return {
    topNav: override.topNav ?? base.topNav,
    primaryCta,
    secondaryCta,
  };
}

export function resolveDiagnosisUnlockChrome(
  partial?: DiagnosisUnlockModalChromePartial,
): DiagnosisUnlockModalChrome {
  const base = getDefaultDiagnosisUnlockChrome();
  if (!partial) {
    return base;
  }
  const step1Merged = mergeStepChrome(base.step1, partial.step1);

  return {
    step1: {
      ...step1Merged,
      showStepProgress:
        partial.step1?.showStepProgress ?? base.step1.showStepProgress,
    },
    step2: mergeStepChrome(base.step2, partial.step2),
    step3: mergeStepChrome(base.step3, partial.step3),
  };
}
